import type { AccessLevel } from "./userAccessService";

// Community/home/messages are gated only for course-only and no-access users. Paused users keep
// community (their course content is locked separately), so 'paused' is NOT community-gated.
export function isCommunityGated(accessLevel: AccessLevel): boolean {
  return accessLevel === "course_only" || accessLevel === "none";
}

/**
 * Fail-open policy for the access gate.
 *
 * The gate must NEVER wall a real, authenticated member whose subscription
 * status could not be determined — boot RPC failed, so `subscriptionStatus` is
 * null but UNKNOWN (not a genuine "no subscription"). Locking out a paying
 * member is far costlier than briefly showing community to a lapsed one: the
 * underlying data is RLS-protected server-side, the paywall here is only a
 * client-side redirect, and the next successful read re-gates a true
 * non-subscriber. So when the status is unknown we return "full" (open).
 *
 * Returns "full" to fail open, or null to run the normal status computation.
 * Impersonation ("view as user") is always strict (returns null) so it still
 * reflects the viewed-as user's real, possibly-locked experience.
 */
export function accessLevelForUnknownStatus(params: {
  isImpersonating: boolean;
  isAuthenticated: boolean;
  /** true once a real profile/subscription status has loaded for the user */
  statusKnown: boolean;
}): AccessLevel | null {
  if (params.isImpersonating) return null;
  if (params.isAuthenticated && !params.statusKnown) return "full";
  return null;
}
